import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Layout } from "../components/layout";
import { SEO } from "../components/SEO";
import { toast } from "sonner";
import { CheckCircle, ArrowRight, Save, RotateCcw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DRAFT_KEY = "itsit_process_draft";

const emptyForm = {
  business_name: "",
  industry: "",
  products_services: "",
  business_goals: "",
  current_website: "",
  has_website: "",
  website_likes: "",
  website_dislikes: "",
  website_purpose: "",
  example_websites: "",
  example_websites_likes: "",
  update_frequency: "",
  additional_info: "",
  contact_name: "",
  phone: "",
  email: "",
};

export const ProcessPage = () => {
  const [navItems, setNavItems] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const autoSaveTimer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/navigation`).then((res) => setNavItems(res.data)).catch(console.error);
    // Restore draft on mount
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        const hasContent = Object.values(draft.data || {}).some((v) => v && v.trim());
        if (hasContent) {
          setFormData(draft.data);
          setDraftSavedAt(draft.savedAt);
          setShowDraftBanner(true);
        }
      }
    } catch (err) { console.debug("Failed to load draft:", err); }
  }, []);

  const saveDraft = useCallback((data) => {
    const now = new Date().toISOString();
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ data, savedAt: now }));
    setDraftSavedAt(now);
  }, []);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_KEY);
    setDraftSavedAt(null);
    setShowDraftBanner(false);
  }, []);

  const handleChange = (e) => {
    const updated = { ...formData, [e.target.name]: e.target.value };
    setFormData(updated);
    // Auto-save after 1.5s of inactivity
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => saveDraft(updated), 1500);
  };

  const handleSaveDraft = () => {
    saveDraft(formData);
    toast.success("Draft saved!");
  };

  const handleDiscardDraft = () => {
    setFormData(emptyForm);
    clearDraft();
    toast("Draft discarded", { icon: <Trash2 size={16} /> });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Clear any pending auto-save timer to prevent re-saving after submission
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }

    try {
      // Transform the data to match the backend schema
      const submissionData = {
        name: formData.contact_name,
        email: formData.email,
        company: formData.business_name,
        phone: formData.phone,
        project_type: formData.industry,
        budget: "",
        timeline: formData.update_frequency,
        description: JSON.stringify({
          products_services: formData.products_services,
          business_goals: formData.business_goals,
          current_website: formData.current_website,
          has_website: formData.has_website,
          website_likes: formData.website_likes,
          website_dislikes: formData.website_dislikes,
          website_purpose: formData.website_purpose,
          example_websites: formData.example_websites,
          example_websites_likes: formData.example_websites_likes,
          update_frequency: formData.update_frequency,
          additional_info: formData.additional_info,
        }, null, 2),
      };
      
      await axios.post(`${API}/process`, submissionData);
      clearDraft();
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    }

    setIsSubmitting(false);
  };

  const steps = [
    {
      number: "01",
      title: "Discovery",
      description: "We learn about your business, goals, and target audience.",
    },
    {
      number: "02",
      title: "Planning",
      description: "We create a detailed roadmap and timeline for your project.",
    },
    {
      number: "03",
      title: "Design",
      description: "We craft beautiful, user-centered designs that align with your brand.",
    },
    {
      number: "04",
      title: "Development",
      description: "We build your website with clean, efficient code.",
    },
    {
      number: "05",
      title: "Launch",
      description: "We deploy your site and provide training on your CMS.",
    },
  ];

  const updateFrequencyOptions = [
    { value: "few-times-year", label: "A few times a year or less" },
    { value: "once-month", label: "Once a month" },
    { value: "1-3-times-month", label: "1-3 times a month" },
    { value: "weekly", label: "Weekly" },
    { value: "multiple-times-week", label: "Multiple times a week" },
    { value: "daily", label: "Daily" },
  ];

  return (
    <Layout navItems={navItems}>
      <SEO
        title="The It's IT Process"
        description="Start your web project with It's IT. Our proven 5-step process ensures your website is delivered on time, on budget, and exceeds expectations."
        keywords="web development process, start a project, website quote, web design consultation, project inquiry"
      />
      {/* Hero */}
      <section data-testid="process-hero" className="section-padding bg-its-teal-dark">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="label-text">The It's IT Process</span>
            <h1 className="heading-1 mt-2 mb-6">
              Let's Build Something
              <span className="text-its-gold"> Amazing</span>
            </h1>
            <p className="body-text">
              Our proven process ensures your project is delivered on time,
              on budget, and exceeds expectations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-padding">
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card text-center relative"
              >
                <div className="text-4xl font-bold text-its-gold/20 mb-2">
                  {step.number}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-its-offwhite/80 text-sm">{step.description}</p>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-its-teal-light">
                    <ArrowRight size={20} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section data-testid="process-form-section" className="section-padding bg-its-teal-dark">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <div className="text-center mb-8">
              <span className="label-text">Business Information Questionnaire</span>
              <h2 className="heading-2 mt-2">Tell Us About Your Vision</h2>
            </div>

            {submitted ? (
              <div className="glass-card text-center py-12">
                <CheckCircle size={64} className="text-its-gold mx-auto mb-4" />
                <h3 className="heading-3 mb-4">Thank You!</h3>
                <p className="body-text">
                  We've received your project details and will be in touch within
                  1-2 business days to discuss next steps.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                
                {/* Draft Restored Banner */}
                {showDraftBanner && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="draft-restored-banner"
                    className="flex items-center justify-between gap-4 rounded-lg border border-its-gold/30 bg-its-gold/10 px-4 py-3"
                  >
                    <div className="flex items-center gap-2 text-sm text-its-gold">
                      <RotateCcw size={16} />
                      <span>Draft restored from {new Date(draftSavedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    <button
                      type="button"
                      onClick={handleDiscardDraft}
                      data-testid="discard-draft-button"
                      className="flex items-center gap-1 text-xs text-its-offwhite/60 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={14} />
                      Discard
                    </button>
                  </motion.div>
                )}
                
                {/* Section 1: Your Business */}
                <div className="glass-card space-y-6">
                  <h3 className="text-xl font-semibold text-its-gold border-b border-its-teal-light/20 pb-3">
                    1. Your Business
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="business_name" className="form-label">Name of your business</label>
                      <input
                        type="text"
                        id="business_name"
                        name="business_name"
                        value={formData.business_name}
                        onChange={handleChange}
                        className="form-input"
                        data-testid="process-input-business-name"
                      />
                    </div>
                    <div>
                      <label htmlFor="industry" className="form-label">Your industry</label>
                      <input
                        type="text"
                        id="industry"
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        className="form-input"
                        data-testid="process-input-industry"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="products_services" className="form-label">What are your products or services?</label>
                    <p className="text-xs text-its-offwhite/60 mb-2">List the top three and/or a more complete list.</p>
                    <textarea
                      id="products_services"
                      name="products_services"
                      rows={3}
                      value={formData.products_services}
                      onChange={handleChange}
                      className="form-input resize-none"
                      data-testid="process-textarea-products"
                    />
                  </div>

                  <div>
                    <label htmlFor="business_goals" className="form-label">Any business goals?</label>
                    <p className="text-xs text-its-offwhite/60 mb-2">Be general or as specific as possible (ex: increase in number of customers, sell "x" number of widgets, more awareness of your company)</p>
                    <textarea
                      id="business_goals"
                      name="business_goals"
                      rows={3}
                      value={formData.business_goals}
                      onChange={handleChange}
                      className="form-input resize-none"
                      data-testid="process-textarea-goals"
                    />
                  </div>
                </div>

                {/* Section 2: Your Website */}
                <div className="glass-card space-y-6">
                  <h3 className="text-xl font-semibold text-its-gold border-b border-its-teal-light/20 pb-3">
                    2. Your Website
                  </h3>

                  <div>
                    <label htmlFor="current_website" className="form-label">Your business website (if you have one)</label>
                    <input
                      type="text"
                      id="current_website"
                      name="current_website"
                      value={formData.current_website}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="https://"
                      data-testid="process-input-current-website"
                    />
                  </div>

                  <div>
                    <label className="form-label">Do you currently have a website?</label>
                    <div className="space-y-2 mt-2">
                      {["Yes", "No"].map((option) => (
                        <label key={option} className="flex items-center gap-3 cursor-pointer group">
                          <input
                            type="radio"
                            name="has_website"
                            value={option.toLowerCase()}
                            checked={formData.has_website === option.toLowerCase()}
                            onChange={handleChange}
                            className="w-4 h-4 text-its-gold bg-its-teal border-its-teal-light/30 focus:ring-its-gold"
                          />
                          <span className="text-its-offwhite group-hover:text-white transition-colors">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {formData.has_website === "yes" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-4 border-l-2 border-its-gold/30">
                      <div>
                        <label htmlFor="website_likes" className="form-label">What do you like about it?</label>
                        <textarea
                          id="website_likes"
                          name="website_likes"
                          rows={2}
                          value={formData.website_likes}
                          onChange={handleChange}
                          className="form-input resize-none"
                        />
                      </div>
                      <div>
                        <label htmlFor="website_dislikes" className="form-label">What don't you like about it?</label>
                        <textarea
                          id="website_dislikes"
                          name="website_dislikes"
                          rows={2}
                          value={formData.website_dislikes}
                          onChange={handleChange}
                          className="form-input resize-none"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="website_purpose" className="form-label">What do you want your website to do, besides provide information?</label>
                    <p className="text-xs text-its-offwhite/60 mb-2">Ex: have a blog, a calendar of events, sell products, gather customer contact info, send a newsletter</p>
                    <textarea
                      id="website_purpose"
                      name="website_purpose"
                      rows={3}
                      value={formData.website_purpose}
                      onChange={handleChange}
                      className="form-input resize-none"
                      data-testid="process-textarea-purpose"
                    />
                  </div>

                  <div>
                    <label htmlFor="example_websites" className="form-label">Share up to three websites in your industry that you like</label>
                    <textarea
                      id="example_websites"
                      name="example_websites"
                      rows={2}
                      value={formData.example_websites}
                      onChange={handleChange}
                      className="form-input resize-none"
                      placeholder="Enter website URLs, one per line"
                    />
                  </div>

                  {formData.example_websites && (
                    <div className="pl-4 border-l-2 border-its-gold/30">
                      <label htmlFor="example_websites_likes" className="form-label">What do you like about them?</label>
                      <textarea
                        id="example_websites_likes"
                        name="example_websites_likes"
                        rows={2}
                        value={formData.example_websites_likes}
                        onChange={handleChange}
                        className="form-input resize-none"
                      />
                    </div>
                  )}

                  <div>
                    <label className="form-label">How often do you want to update your website content?</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      {updateFrequencyOptions.map((option) => (
                        <label key={option.value} className="flex items-center gap-3 cursor-pointer group">
                          <input
                            type="radio"
                            name="update_frequency"
                            value={option.value}
                            checked={formData.update_frequency === option.value}
                            onChange={handleChange}
                            className="w-4 h-4 text-its-gold bg-its-teal border-its-teal-light/30 focus:ring-its-gold"
                          />
                          <span className="text-its-offwhite group-hover:text-white transition-colors text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Section 3: Additional Information */}
                <div className="glass-card space-y-6">
                  <h3 className="text-xl font-semibold text-its-gold border-b border-its-teal-light/20 pb-3">
                    3. Additional Information
                  </h3>

                  <div>
                    <label htmlFor="additional_info" className="form-label">Anything else you'd like to share?</label>
                    <textarea
                      id="additional_info"
                      name="additional_info"
                      rows={4}
                      value={formData.additional_info}
                      onChange={handleChange}
                      className="form-input resize-none"
                      placeholder="Any other details, questions, or information you'd like us to know..."
                      data-testid="process-textarea-additional"
                    />
                  </div>
                </div>

                {/* Section 4: Contact Info */}
                <div className="glass-card space-y-6">
                  <h3 className="text-xl font-semibold text-its-gold border-b border-its-teal-light/20 pb-3">
                    4. Contact Info
                  </h3>

                  <div>
                    <label htmlFor="contact_name" className="form-label">Name and/or business name</label>
                    <input
                      type="text"
                      id="contact_name"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="process-input-contact-name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="form-label">Phone</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                        data-testid="process-input-phone"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="form-label">E-mail</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        data-testid="process-input-email"
                      />
                    </div>
                  </div>
                </div>

                {/* Auto-save indicator */}
                {draftSavedAt && !showDraftBanner && (
                  <p data-testid="autosave-indicator" className="text-xs text-its-offwhite/40 text-center">
                    Auto-saved {new Date(draftSavedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                  </p>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    data-testid="save-draft-button"
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-its-gold/40 text-its-gold hover:bg-its-gold/10 transition-colors font-medium sm:w-auto"
                  >
                    <Save size={18} />
                    Save Draft
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="process-submit-button"
                    className="btn-primary flex-1 disabled:opacity-50"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Questionnaire"}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default ProcessPage;
